import { nanoid } from 'nanoid/non-secure';
import { uploadData } from 'aws-amplify/storage';
import outputs from "../../amplify_outputs.json";
import { generateS3AssetUrl } from "./FormFileServer";

/**
 * Imagenes publicas de las plantillas de correo.
 *
 * El HTML de la plantilla lo envia un servicio externo y lo renderiza el cliente de correo
 * del destinatario, que no tiene credenciales de Cognito. Por eso estas imagenes no pueden
 * usar las URL firmadas de `getUrl()` (ademas expiran) ni `data:` URI (los bloquean casi
 * todos los clientes de correo, e inflarian el item de DynamoDB donde vive la plantilla).
 *
 * En su lugar viven bajo el prefijo `email-assets/` del bucket de la app, que CloudFront
 * publica de forma anonima (ver `amplify/infra/email-assets-cdn.ts`).
 */

/** Prefijo del bucket que CloudFront publica. Es tambien su `originPath`. */
const EMAIL_ASSETS_PREFIX = "email-assets/";

/** Dominio de la distribucion, publicado por el backend en `custom.EmailAssetsCdnUrl`. */
export const EMAIL_ASSETS_CDN_URL = (
    outputs as unknown as { custom?: { EmailAssetsCdnUrl?: string } }
).custom?.EmailAssetsCdnUrl ?? "";

export const ALLOWED_EMAIL_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/** Tope deliberadamente bajo: son imagenes que se descargan dentro de un correo. */
export const MAX_EMAIL_IMAGE_BYTES = 2 * 1024 * 1024;

/** Deja el nombre en algo seguro para una llave de S3 y para una URL. */
const safeFileName = (fileName: string): string =>
    (fileName || "imagen")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9.-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase() || "imagen";

/**
 * Llave de una imagen subida desde el editor. El `nanoid` la hace unica, lo que permite
 * cachearla indefinidamente en CloudFront.
 */
export const emailUploadKey = (tenantId: string, fileName: string): string =>
    `${EMAIL_ASSETS_PREFIX}uploads/${tenantId}/${nanoid(10)}-${safeFileName(fileName)}`;

/**
 * Llave del logo de la empresa. Es **fija** a proposito: asi la URL dentro de las plantillas
 * no cambia nunca y cambiar el logo en el perfil se refleja en todos los correos sin
 * reeditarlos. El comportamiento `logos/*` de CloudFront la cachea solo 5 minutos.
 */
export const emailLogoKey = (tenantId: string): string =>
    `${EMAIL_ASSETS_PREFIX}logos/${tenantId}`;

/** Traduce una llave del bucket a la URL publica servida por CloudFront. */
export const emailAssetPublicUrl = (key: string): string => {
    if (!EMAIL_ASSETS_CDN_URL) {
        throw new Error("No hay un CDN configurado para las imagenes de correo");
    }
    const path = key.startsWith(EMAIL_ASSETS_PREFIX) ? key.slice(EMAIL_ASSETS_PREFIX.length) : key;
    return `${EMAIL_ASSETS_CDN_URL.replace(/\/+$/, "")}/${path}`;
};

/**
 * Sube una imagen del editor y devuelve su URL publica definitiva.
 * @throws Error con un mensaje para el usuario si el tipo o el tamano no son validos.
 */
export const uploadEmailImage = async (
    file: Blob,
    tenantId: string,
    fileName: string,
): Promise<string> => {
    if (!ALLOWED_EMAIL_IMAGE_TYPES.includes(file.type)) {
        throw new Error("Formato no permitido. Use PNG, JPG, GIF o WEBP");
    }
    if (file.size > MAX_EMAIL_IMAGE_BYTES) {
        throw new Error("La imagen supera el maximo de 2 MB");
    }

    const key = emailUploadKey(tenantId, fileName);
    await uploadData({
        path: key,
        data: file,
        options: { contentType: file.type },
    }).result;

    return emailAssetPublicUrl(key);
};

/**
 * Copia el logo privado del perfil a la llave publica fija del tenant y devuelve su URL.
 *
 * Es idempotente (siempre sobrescribe la misma llave) y se invoca desde dos lados: al
 * guardar el perfil con un logo nuevo, y al insertarlo desde el editor -- este segundo caso
 * cubre a los tenants que ya tenian logo antes de que existiera el espejo.
 *
 * @param privateLogoKey llave de `Suscription.logo` (`suscription/<tenantId>/...`)
 * @param blob contenido ya en memoria, cuando el logo se acaba de subir desde el perfil
 */
export const mirrorLogoToEmailAssets = async (
    tenantId: string,
    privateLogoKey: string,
    blob?: Blob,
): Promise<string> => {
    if (!privateLogoKey) {
        throw new Error("La suscripcion no tiene un logo configurado");
    }

    let data = blob;
    if (!data) {
        const signed = await generateS3AssetUrl(privateLogoKey);
        const response = await fetch(signed.url);
        if (!response.ok) {
            throw new Error("No fue posible leer el logo de la empresa");
        }
        data = await response.blob();
    }

    const key = emailLogoKey(tenantId);
    await uploadData({
        path: key,
        data,
        // Sin contentType el objeto quedaria como application/octet-stream y los clientes
        // de correo no lo pintarian.
        options: { contentType: data.type || "image/png" },
    }).result;

    return emailAssetPublicUrl(key);
};
